import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { ALBUM_KIND, isMediaFile, isVideoEntry, manifestTags, sniffMimeByName, type AlbumFileEntry, type AlbumManifest } from '@/lib/album';
import { b64uToBytes, deriveAlbumKey, encryptBytes, sha256Hex } from '@/lib/albumCrypto';
import { extractVideoFrame } from '@/lib/videoThumb';

interface AlbumUploaderProps {
  manifest: AlbumManifest;
  masterKeyB64u: string;
}

/** Encrypts photos/videos locally, uploads the ciphertext to Blossom, then republishes the manifest. */
export function AlbumUploader({ manifest, masterKeyB64u }: AlbumUploaderProps) {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<string | null>(null);

  async function handleFiles(list: FileList) {
    const files = Array.from(list).filter(isMediaFile);
    if (files.length === 0) {
      toast({ title: 'No supported files', description: 'Only photos and videos can be added.', variant: 'destructive' });
      return;
    }
    try {
      const key = await deriveAlbumKey(b64uToBytes(masterKeyB64u), manifest.salt, manifest.d);
      const newEntries: AlbumFileEntry[] = [];
      let i = 0;
      for (const file of files) {
        i++;
        setStatus(`Encrypting ${i}/${files.length}: ${file.name}`);
        const bytes = new Uint8Array(await file.arrayBuffer());
        const { cipher, nonce } = await encryptBytes(key, bytes);
        const sha = await sha256Hex(cipher);
        setStatus(`Uploading ${i}/${files.length}: ${file.name}`);
        const encFile = new File([cipher], `${file.name}.locket`, { type: 'application/octet-stream' });
        const tags = await uploadFile(encFile);
        const url = tags.find(([n]) => n === 'url')?.[1];
        const x = tags.find(([n]) => n === 'x')?.[1];
        if (!url) throw new Error(`Upload of ${file.name} failed`);

        // Videos: extract a frame, encrypt and upload it as the tile thumbnail.
        const mime = file.type || sniffMimeByName(file.name);
        let thumbUrl: string | undefined;
        let thumbNonce: string | undefined;
        if (isVideoEntry({ mime, name: file.name })) {
          setStatus(`Creating thumbnail ${i}/${files.length}: ${file.name}`);
          const frame = await extractVideoFrame(file);
          if (frame) {
            const t = await encryptBytes(key, new Uint8Array(await frame.arrayBuffer()));
            const tTags = await uploadFile(
              new File([t.cipher], `${file.name}.thumb`, { type: 'application/octet-stream' }),
            );
            thumbUrl = tTags.find(([n]) => n === 'url')?.[1];
            if (thumbUrl) thumbNonce = t.nonce;
          }
        }

        newEntries.push({
          url,
          sha256: x ?? sha,
          nonce,
          mime,
          size: file.size,
          name: file.name,
          thumbUrl,
          thumbNonce,
        });
      }
      setStatus('Publishing album…');
      await publish({
        kind: ALBUM_KIND,
        content: '',
        tags: manifestTags({
          d: manifest.d,
          title: manifest.title,
          salt: manifest.salt,
          cover: manifest.cover,
          parent: manifest.parent,
          encryptedKey: manifest.encryptedKey,
          subs: manifest.subs,
          files: [...manifest.files, ...newEntries],
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['album'] });
      queryClient.invalidateQueries({ queryKey: ['my-albums'] });
      toast({ title: 'Upload complete', description: `${newEntries.length} file(s) encrypted and added.` });
    } catch (e) {
      console.error(e);
      toast({ title: 'Upload failed', description: 'One or more files could not be added.', variant: 'destructive' });
    } finally {
      setStatus(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)}
      />
      <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={status !== null}>
        {status ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
        {status ?? 'Add photos / videos'}
      </Button>
    </div>
  );
}
